<script setup lang="ts">
  import type { QFile } from 'quasar'

  interface Props {
    /**
     * Поддерживаемые форматы. Пример '.csv,.xls,.xlsx'
     */
    formats?: string
    /**
     * Максимальный размер файла в байтах
     */
    maxFileSize?: number
    loading?: boolean
  }

  interface Emits {
    (e: 'validationError', data: 'format' | 'size'): void
  }
  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const file = defineModel<File>()
  const allowedExtensions = computed(() => props.formats?.split(',') || [])

  const fileInput = useTemplateRef<QFile>('fileInput')
  const isDragging = ref(false)

  const validateFileInfo = (newFile: File) => {
    const fileName = newFile.name.toLowerCase()
    const isValidExt = allowedExtensions.value.length
      ? allowedExtensions.value.some((ext) => fileName.endsWith(ext))
      : true
    if (!isValidExt) {
      emit('validationError', 'format')
    }
    const isValidSize = props.maxFileSize !== undefined ? newFile.size < props.maxFileSize : true
    if (!isValidSize) {
      emit('validationError', 'size')
    }
    return isValidExt && isValidSize
  }

  const onDragOver = () => {
    isDragging.value = true
  }

  const onDragLeave = () => {
    isDragging.value = false
  }

  const onDrop = async (e: DragEvent) => {
    isDragging.value = false
    const droppedFile = e.dataTransfer?.files[0]
    if (droppedFile && validateFileInfo(droppedFile)) {
      file.value = droppedFile
    }
  }

  const onFileSelected = (selectedFile: File) => {
    if (selectedFile && validateFileInfo(selectedFile)) {
      file.value = selectedFile
    }
  }
  const addFile = () => {
    fileInput.value?.pickFiles()
  }
</script>

<template>
  <div>
    <div
      v-if="!file || loading"
      class="ease flex min-h-[275px] w-full cursor-pointer flex-col items-center justify-center gap-y-2 rounded-xl border border-(--border-border-secondary) p-4 transition-all duration-300 hover:bg-(--background-bg-surface-new)"
      :class="{
        'bg-(--background-bg-surface-new)': isDragging,
        'bg-(--background-bg-secondary)': !isDragging,
      }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="addFile"
    >
      <QFile
        ref="fileInput"
        :model-value="file"
        :accept="formats"
        :max-file-size="maxFileSize"
        class="hidden"
        @update:model-value="onFileSelected"
      />
      <QSpinner
        v-if="loading"
        size="80px"
        class="text-(--text-content)"
      />
      <slot v-else>
        <i class="fa-light fa-cloud-arrow-up text-[80px] text-(--text-content)" />
        <div
          class="max-w-[247px] text-center text-base text-(--text-content)"
          v-html="$t('base_dragndrop_description')"
        />
      </slot>
    </div>
    <slot
      v-else
      name="file-view"
      :file="file"
    />
  </div>
</template>

<style lang="scss" scoped></style>
